/**
 * Client-side Pricing Configuration
 * 
 * This mirrors the server-side pricing configuration.
 * Keep this in sync with server/config/pricing.js
 */

const PRICING_CONFIG = {
  // Core pricing values (in dollars for display)
  baseRatePerPhoto: 0.01,  // $0.01 per photo
  minimumAmount: 2.99,     // $2.99 minimum charge
  currency: 'usd',

  // Free trial settings
  freeTrial: {
    maxPhotos: 1000,
    enabled: true
  },

  /**
   * Calculate payment amount for a given number of photos
   * @param {number} photoCount - Number of photos to process
   * @returns {number} Amount in dollars
   */
  calculateAmount(photoCount) {
    const calculatedAmount = photoCount * this.baseRatePerPhoto;
    return Math.max(calculatedAmount, this.minimumAmount);
  },

  /**
   * Calculate payment amount considering free trial usage
   * @param {number} photoCount - Number of photos to process
   * @param {number} remainingFreePhotos - Number of free photos remaining
   * @returns {number} Amount in dollars for photos beyond free trial
   */
  calculateAmountWithFreeTrial(photoCount, remainingFreePhotos) {
    if (photoCount <= remainingFreePhotos) {
      return 0; // All photos covered by free trial
    }
    
    const paidPhotoCount = photoCount - remainingFreePhotos;
    const calculatedAmount = paidPhotoCount * this.baseRatePerPhoto;
    return Math.max(calculatedAmount, this.minimumAmount);
  },

  /**
   * Get the maximum number of free trial photos
   * @returns {number} Maximum free trial photos
   */
  getMaxFreePhotos() {
    return this.freeTrial.maxPhotos;
  },

  /**
   * Format amount for display
   * @param {number} amount - Amount in dollars
   * @returns {string} Formatted price (e.g., "$1.50")
   */
  formatPrice(amount) {
    return `$${amount.toFixed(2)}`;
  },

  /**
   * Get the per-photo rate as a display string
   * @returns {string} Formatted rate (e.g., "$0.01")
   */
  getDisplayRate() {
    return this.formatPrice(this.baseRatePerPhoto);
  },

  /**
   * Get the minimum charge as a display string
   * @returns {string} Formatted minimum (e.g., "$1.00")
   */
  getMinimumDisplay() {
    return this.formatPrice(this.minimumAmount);
  },

  /**
   * Calculate and format total price for display
   * @param {number} photoCount - Number of photos
   * @returns {string} Formatted total price
   */
  calculateDisplayPrice(photoCount) {
    const amount = this.calculateAmount(photoCount);
    return this.formatPrice(amount);
  },

  /**
   * Get pricing examples for display
   * @returns {Array} Array of pricing examples
   */
  getPricingExamples() {
    const examples = [5, 25, 100, 500];
    return examples.map(count => ({
      photos: count,
      price: this.calculateDisplayPrice(count),
      amount: this.calculateAmount(count)
    }));
  }
};

export default PRICING_CONFIG;
